import type { FC } from "react"
import { Line } from "recharts"
import capitalize from "lodash.capitalize"

import type { Account } from "query/graphql"
import { VoteType } from "query/graphql"
import { getVoteStyles } from "common/helpers/voteStyle"
import { bigNumberToNumber } from "common/helpers/bignumber"
import { MIN_VOTES_TO_SHOW_AVATAR } from "common/constants/chart"
import { ROUTES } from "common/constants/routes"
import UserAvatar from "common/components/UserAvatar"
import type { TimelineDot } from "proposal/helpers/chart"

type VotingTimelineLineProps = {
  supports?: VoteType[]
  voters: Pick<Account, "name" | "address" | "picture">[]
  decimals: number
}

const VotingTimelineLine: FC<VotingTimelineLineProps> = ({
  supports = [VoteType.For, VoteType.Against, VoteType.Abstain],
  voters,
  decimals,
}) => {
  const voteStyles = getVoteStyles(supports)

  return (
    <>
      {supports.map((support) => {
        const label = capitalize(support)
        const { color } = voteStyles[support]
        const lowerSupport = support.toLowerCase()
        const dataKey = `${lowerSupport}Count`

        return (
          <Line
            key={`ballot_support_line_${support}`}
            activeDot={false}
            dataKey={(item) =>
              item.supportTotals[support]
                ? bigNumberToNumber(item.supportTotals[support], decimals)
                : 0
            }
            dot={
              <Dot
                // @ts-expect-error The library doesn't provide correct typings
                dataKey={dataKey}
                decimals={decimals}
                support={support}
                voters={voters}
              />
            }
            name={label}
            stroke={color}
            strokeWidth={2}
          />
        )
      })}
    </>
  )
}

export default VotingTimelineLine

type DotProps = {
  cx: number
  cy: number
  voters: Array<Pick<Account, "picture" | "address" | "name">>
  payload: TimelineDot
  support: VoteType
  decimals: number
}

const Dot: FC<DotProps> = ({
  cy,
  cx,
  voters,
  payload,
  support: lineSupport,
  decimals,
}) => {
  if (payload === undefined) return null

  const { voter: voterAddress, type, amount } = payload

  const voter = voters.find(({ address }) => address === voterAddress)

  if (!voter) return null

  const { picture } = voter

  const isCorrectLine = type === lineSupport

  const isErc20 = decimals !== 0

  const hasMinimumVotes = isErc20 ? amount >= MIN_VOTES_TO_SHOW_AVATAR : true

  return (
    <svg height={24} width={24} x={cx - 10} y={cy - 10}>
      <a href={ROUTES.profile(voterAddress)}>
        <g>
          {isCorrectLine && hasMinimumVotes ? (
            <foreignObject height="24px" width="24px">
              <UserAvatar address={voterAddress} size="xs" src={picture} />
            </foreignObject>
          ) : null}
        </g>
      </a>
    </svg>
  )
}
